import { ReactNode } from "react";

export type ReportFailureMessageType = 'warning' | 'error';

export type ReportMessage = {
    message: string;
    type: ReportFailureMessageType;
};

export const ReportValidationResultMessages: Record<string, string> = {
    unauthorized_user_role: "Your access level is view only. Reach out to your account's admin if you wish to upgrade your member type in order to rerun reports.",
    subscription_not_found: "This report is no longer a part of your subscription. Reach out to your account executive if you wish to upgrade your subscription.",
    category_subscription_not_found: "This report cannot be run due to the following reason: category(s) no longer in subscription. Please select a new category to rerun this report.",
    category_subscription_not_found_multi: "This report cannot be run due to the following reason: category(s) removed and market access validations need rerun. Please review your selected categories and reselect your market(s) before rerunning this report.",
    not_prior_approval: "This report cannot be run due to the following reason: market(s) require prior approval. Please select a new market to rerun this report or reach out to your account executive to obtain approval.",
    not_rev_share: "This report cannot be run due to the following reason: premium market purchase required. Please select a new market to rerun this report or reach out to your account executive to purchase the market.",
    market_not_accessible: "This report cannot be run due to the following reason: market(s) no longer in subscription. Please select a new market to rerun this report.",
    market_not_found: "This report cannot be run due to the following reason: market(s) no longer exists. Please select a new market to rerun this report.",
    attribute_group_not_found: "This report cannot be run due to the following reason: Attribute Group(s) no longer in subscription. Please select a new Attribute Group to rerun this report.",
    market_not_found_multi: "Your market access has changed since you last ran this report. Please navigate to the Markets step to verify your selections.",
    custom_char_not_found: "This report cannot be run due to the following reason: Characteristic filter selected no longer exists. Please review your selections before rerunning this report.",
    default: "You cannot run this report because you have not selected all of the required values."
};

export const ReportFailureMessages: Record<string, ReportMessage> = {
    unauthorized_user_role: {
        message: "User who scheduled the report was changed to viewer.",
        type: "warning"
    },
    invalid_user_role: {
        message: "User who scheduled the report was changed to viewer.",
        type: "warning"
    },
    no_data_rms: {
        message: "No data for the selections you've made.",
        type: "warning"
    },
    no_data_cps: {
        message: "Not enough sample for the selections you've made.",
        type: "warning"
    },
    subscription_not_found: {
        message: "Subscription access level changed so can no longer access the report.",
        type: "warning"
    },
    category_subscription_not_found: {
        message: "Subscription categories changed.",
        type: "warning"
    },
    not_prior_approval: {
        message: "Lost access to PMA market.",
        type: "warning"
    },
    not_rev_share: {
        message: "Lost access to premium market.",
        type: "warning"
    },
    market_not_accessible: {
        message: " Lost access to accessible market.",
        type: "warning"
    },
    market_not_found: {
        message: "Market no longer exists.",
        type: "error"
    },
    attribute_group_not_found: {
        message: "Lost access to attribute group.",
        type: "warning"
    },
    custom_char_not_found: {
        message: "Characteristic no longer exists.",
        type: "error"
    },
    no_credits_left: {
        message: "No run credits left.",
        type: "warning"
    },
    unknown_error: {
        message: "An unknown error occurred while validating the report.",
        type: "error"
    }
};

export const DEFAULT_REPORT_VALIDATION_RESULT_WARNING_SUBMESSAGE = "Please try different selections in order to run the report.";

export const DEFAULT_REPORT_VALIDATION_RESULT_WARNING_MESSAGE = "There is no data for the selections you've made.";

export const DEFAULT_REPORT_VALIDATION_RESULT_WARNING_CONTENT = (
    <>
        <p>{DEFAULT_REPORT_VALIDATION_RESULT_WARNING_MESSAGE}</p>
        <p>{DEFAULT_REPORT_VALIDATION_RESULT_WARNING_SUBMESSAGE}</p>
    </>
);

export const ReportValidationResultWarningMessages: Record<datatype, ReactNode> = {
    'cps': DEFAULT_REPORT_VALIDATION_RESULT_WARNING_CONTENT,
    'rms': DEFAULT_REPORT_VALIDATION_RESULT_WARNING_CONTENT,
    'omni': (
        <>
            <p>There is no data or not enough sample for the selections you've made.</p>
            <p>{DEFAULT_REPORT_VALIDATION_RESULT_WARNING_SUBMESSAGE}</p>
        </>
    ),
    'osh': (
        <>
            <p>There is either no data or not enough sample for your selections.</p>
            <p>Please select a larger scope to run your report.</p>
        </>
    ),
}